'use client';

import { useCallback, useMemo, useState } from 'react';
import styles from './TeamCarousel.module.scss';
import { gaClick } from '@/src/helpers/gtag';
import { proxifyImageUrl } from '@/src/helpers/proxifyImageUrl';

/* ── Inline SVG icons ── */
const IconDelivery = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    <path d="M12 11v4" />
    <path d="M8 15h8" />
  </svg>
);

const IconWeb = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const IconData = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const IconProduct = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const IconChevronLeft = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevronRight = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconAgile = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ICONS: Record<string, () => JSX.Element> = {
  web: IconWeb,
  data: IconData,
  delivery: IconDelivery,
  product: IconProduct,
  agile: IconAgile,
};

const DEFAULT_BG = 'https://ik.imagekit.io/lindahestia/articles/equipe-analyse-ecran.webp';

/* ── Data ── */
export interface TeamMemberData {
  id: string;
  role: string;
  subtitle: string;
  icon: string;
  image?: string;
  imageAlt?: string;
}

export interface TeamCarouselProps {
  members?: TeamMemberData[];
  backgroundImage?: string;
  caption?: string;
}

interface TeamMember {
  id: string;
  role: string;
  subtitle: string;
  icon: JSX.Element;
  image?: string;
  imageAlt?: string;
}

const DEFAULT_MEMBERS: TeamMemberData[] = [
  { id: 'web', role: 'Développeur Web', subtitle: 'Full-stack & Cloud', icon: 'web' },
  { id: 'data', role: 'Data Engineer', subtitle: 'Pipelines & ML', icon: 'data' },
  {
    id: 'delivery',
    role: 'Responsable Delivery',
    subtitle: 'Votre interlocuteur unique',
    icon: 'delivery',
  },
  { id: 'product', role: 'Product Owner', subtitle: 'Vision & priorisation', icon: 'product' },
  { id: 'agile', role: 'Coach Agile', subtitle: 'Sprints & rétros', icon: 'agile' },
];

const resolveMembers = (
  members?: TeamMemberData[],
  backgroundImage?: string
): { resolved: TeamMember[]; bg: string } => {
  const source = members && members.length > 0 ? members : DEFAULT_MEMBERS;
  const resolved = source.map((m) => {
    const Icon = ICONS[m.icon] || IconDelivery;
    return { ...m, icon: <Icon /> };
  });
  return { resolved, bg: proxifyImageUrl(backgroundImage || DEFAULT_BG) };
};

/* ── Helpers ── */
const getSlot = (memberId: string, focusedId: string, order: string[]): number => {
  const idx = order.indexOf(memberId);
  const focusIdx = order.indexOf(focusedId);
  let diff = idx - focusIdx;
  if (diff < -2) {
    diff += order.length;
  }
  if (diff > 2) {
    diff -= order.length;
  }
  return diff;
};

/* ── Single Card ── */
const RoleCard = ({
  member,
  focusedId,
  order,
  backgroundImage,
  onClick,
}: {
  member: TeamMember;
  focusedId: string;
  order: string[];
  backgroundImage: string;
  onClick?: () => void;
}) => {
  const slot = useMemo(() => getSlot(member.id, focusedId, order), [member.id, focusedId, order]);
  const isCenter = slot === 0;
  const isDelivery = member.id === 'delivery';
  const cardBg = proxifyImageUrl(member.image || backgroundImage);

  return (
    <div
      className={`${styles.card} ${styles[`slot${slot}`]} ${isDelivery ? styles.deliveryCard : ''}`}
      data-card={member.id}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div
        className={styles.cardBg}
        style={{ backgroundImage: `url(${cardBg})` }}
        role="img"
        aria-label={member.imageAlt || member.role}
      />
      <div className={styles.cardOverlay} />
      <div className={styles.cardInner}>
        <div className={styles.cardIcon}>{member.icon}</div>
        <h3 className={styles.cardRole}>{member.role}</h3>
        <p className={`${styles.cardSubtitle} ${isCenter ? styles.subtitleVisible : ''}`}>
          {member.subtitle}
        </p>
      </div>
    </div>
  );
};

/* ── Carousel ── */
export const TeamCarousel = ({ members, backgroundImage, caption }: TeamCarouselProps) => {
  const { resolved, bg } = useMemo(
    () => resolveMembers(members, backgroundImage),
    [members, backgroundImage]
  );
  const order = useMemo(() => resolved.map((m) => m.id), [resolved]);
  const [focusedId, setFocusedId] = useState<string>(
    resolved.find((m) => m.id === 'delivery')?.id || resolved[0]?.id || ''
  );

  const handlePrev = useCallback(() => {
    setFocusedId((prev) => {
      const idx = order.indexOf(prev);
      return order[(idx - 1 + order.length) % order.length];
    });
  }, [order]);

  const handleNext = useCallback(() => {
    setFocusedId((prev) => {
      const idx = order.indexOf(prev);
      return order[(idx + 1) % order.length];
    });
  }, [order]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        gaClick('team_carousel_prev');
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        gaClick('team_carousel_next');
        handleNext();
      }
    },
    [handlePrev, handleNext]
  );

  return (
    <div className={styles.wrapper}>
      {/* WAI-ARIA carousel pattern: focusable region with arrow-key navigation */}
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        className={styles.scene}
        onKeyDown={handleKeyDown}
        role="region"
        aria-roledescription="carousel"
        aria-label="Équipe pluridisciplinaire"
        tabIndex={0}
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonLeft}`}
          onClick={() => {
            gaClick('team_carousel_prev');
            handlePrev();
          }}
          aria-label="Membre précédent"
        >
          <IconChevronLeft />
        </button>

        <div className={styles.track}>
          {resolved.map((member) => (
            <RoleCard
              key={member.id}
              member={member}
              focusedId={focusedId}
              order={order}
              backgroundImage={bg}
              onClick={() => {
                gaClick('team_carousel_card', { role: member.role });
                setFocusedId(member.id);
              }}
            />
          ))}
        </div>

        <div className={styles.mobileDots} aria-label="Choisir une ressource">
          {resolved.map((member) => (
            <button
              key={`${member.id}-dot`}
              type="button"
              className={`${styles.mobileDot} ${
                member.id === focusedId ? styles.mobileDotActive : ''
              }`}
              onClick={() => {
                gaClick('team_carousel_dot', { role: member.role });
                setFocusedId(member.id);
              }}
              aria-label={`Afficher ${member.role}`}
              aria-current={member.id === focusedId ? 'true' : undefined}
            />
          ))}
        </div>

        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonRight}`}
          onClick={() => {
            gaClick('team_carousel_next');
            handleNext();
          }}
          aria-label="Membre suivant"
        >
          <IconChevronRight />
        </button>
      </div>
      {caption && <p className={styles.caption}>{caption}</p>}
    </div>
  );
};
